import React, { useEffect, useRef, useState } from "react";

export type Commend3RowAction = "copy" | "unpromote" | "delete";

export type Commend3RowActionItem = {
  type: Commend3RowAction;
  label: string;
  disabled?: boolean;
  danger?: boolean;
  hint?: string;
};

type RowActionsMenuProps = {
  id: string;
  actions: Commend3RowActionItem[];
  openUpward: boolean;
  onAction: (action: Commend3RowAction) => void;
};

const iconProps = {
  viewBox: "0 0 24 24",
  style: {
    stroke: "currentColor",
    fill: "none",
    strokeWidth: 1.5,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  },
  className: "tw-h-3.5 tw-w-3.5 tw-shrink-0",
  "aria-hidden": true,
};

const ACTION_ICONS: Record<Commend3RowAction, React.ReactNode> = {
  copy: (
    <svg {...iconProps}>
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  ),
  unpromote: (
    <svg {...iconProps}>
      <path d="M21 3v5h-5" />
      <path d="M21 8A9 9 0 1 0 12 21" />
      <path d="M16 16l5 5M21 16l-5 5" />
    </svg>
  ),
  delete: (
    <svg {...iconProps}>
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  ),
};

const RowActionsMenu = ({ id, actions, openUpward, onAction }: RowActionsMenuProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", away);
    return () => document.removeEventListener("click", away);
  }, [open]);

  return (
    <div ref={wrapRef} className="tw-relative">
      <span
        id={`${id}-trigger`}
        role="button"
        tabIndex={0}
        className="tw-cursor-pointer tw-px-1 tw-text-xl tw-leading-none tw-text-[#B4B9D4]"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
        aria-label="Row actions"
      >
        ⋮
      </span>

      {open ? (
        <div
          id={`${id}-menu`}
          className="tw-absolute tw-right-[10px] tw-z-[60] tw-min-w-[130px] tw-overflow-hidden tw-rounded-md tw-border-[0.5px] tw-border-[#E3E5EC] tw-bg-white"
          style={{
            boxShadow: "0 4px 20px rgba(0,0,0,.14)",
            ...(openUpward ? { bottom: "32px" } : { top: "32px" }),
          }}
        >
          {actions.map((action, index) => (
            <div
              key={action.type}
              id={`${id}-${action.type}`}
              title={action.disabled ? action.hint : undefined}
              className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-[9px] tw-text-[13px]"
              style={{
                borderTop: index === 0 ? "none" : "0.5px solid #F4F5F9",
                color: action.disabled
                  ? "#B4B9D4"
                  : action.danger
                  ? "#D60027"
                  : "#0B0C23",
                cursor: action.disabled ? "not-allowed" : "pointer",
                opacity: action.disabled ? 0.6 : 1,
              }}
              onClick={() => {
                if (action.disabled) return;
                setOpen(false);
                onAction(action.type);
              }}
            >
              {ACTION_ICONS[action.type]}
              {action.label}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default RowActionsMenu;
